import { cn } from '@/lib/utils';

/**
 * The PMS loader (charte, composant 09): the brand circle - six arcs, six
 * noyaux, six breathing gaps - rotating counter-clockwise. This is the one
 * place the six colours belong together: the system itself is what is
 * working, so it is level 3 of the colour hierarchy rather than a decoration.
 *
 * Geometry is quoted from the design system: 100x100 viewBox, r=44, each arc
 * spanning 52 degrees with an 8-degree gap, `stroke-linecap: round`, and a
 * gradient fading each arc into the white gap so no hard step appears.
 * Rotation transforms the whole SVG, never the individual arcs.
 */

const ARCS = [
  {
    id: 'bleu-ciel',
    color: 'var(--color-bleu-ciel)',
    d: 'M 30.71 10.45 A 44 44 0 0 1 69.29 10.45',
    x1: 30.71,
    y1: 10.45,
    x2: 69.29,
    y2: 10.45,
  },
  {
    id: 'vert',
    color: 'var(--color-vert)',
    d: 'M 74.60 13.52 A 44 44 0 0 1 93.89 46.93',
    x1: 74.6,
    y1: 13.52,
    x2: 93.89,
    y2: 46.93,
  },
  {
    id: 'jaune',
    color: 'var(--color-jaune)',
    d: 'M 93.89 53.07 A 44 44 0 0 1 74.60 86.48',
    x1: 93.89,
    y1: 53.07,
    x2: 74.6,
    y2: 86.48,
  },
  {
    id: 'rouge',
    color: 'var(--color-rouge)',
    d: 'M 69.29 89.55 A 44 44 0 0 1 30.71 89.55',
    x1: 69.29,
    y1: 89.55,
    x2: 30.71,
    y2: 89.55,
  },
  {
    id: 'rose',
    color: 'var(--color-rose)',
    d: 'M 25.40 86.48 A 44 44 0 0 1 6.11 53.07',
    x1: 25.4,
    y1: 86.48,
    x2: 6.11,
    y2: 53.07,
  },
  {
    id: 'bleu-nuit',
    color: 'var(--color-bleu-nuit)',
    d: 'M 6.11 46.93 A 44 44 0 0 1 25.40 13.52',
    x1: 6.11,
    y1: 46.93,
    x2: 25.4,
    y2: 13.52,
  },
] as const;

/** Stroke width scales inversely with the diameter, per the DS size table. */
const SIZES = {
  xs: { className: 'size-4', stroke: 12.5 },
  sm: { className: 'size-6', stroke: 12.5 },
  md: { className: 'size-10', stroke: 11.25 },
  lg: { className: 'size-16', stroke: 10.9 },
} as const;

interface PmsSpinnerProps {
  /** What the wait means - read out by assistive tech. */
  label: string;
  size?: keyof typeof SIZES;
  className?: string;
}

export function PmsSpinner({ label, size = 'md', className }: PmsSpinnerProps) {
  const { className: sizeClass, stroke } = SIZES[size];
  return (
    <output aria-label={label} className={cn('inline-flex', className)}>
      <svg
        viewBox="0 0 100 100"
        aria-hidden="true"
        focusable="false"
        className={cn('pms-spinner', sizeClass)}
      >
        <defs>
          {ARCS.map((arc) => (
            <linearGradient
              key={arc.id}
              id={`pms-spinner-${arc.id}`}
              gradientUnits="userSpaceOnUse"
              x1={arc.x1}
              y1={arc.y1}
              x2={arc.x2}
              y2={arc.y2}
            >
              <stop offset="0%" stopColor={arc.color} stopOpacity="1" />
              <stop offset="30%" stopColor={arc.color} stopOpacity="1" />
              <stop offset="50%" stopColor={arc.color} stopOpacity="0.58" />
              <stop offset="65%" stopColor={arc.color} stopOpacity="0.25" />
              <stop offset="80%" stopColor={arc.color} stopOpacity="0.07" />
              <stop offset="100%" stopColor={arc.color} stopOpacity="0" />
            </linearGradient>
          ))}
        </defs>
        <g strokeLinecap="round" strokeWidth={stroke} fill="none">
          {ARCS.map((arc) => (
            <path key={arc.id} d={arc.d} stroke={`url(#pms-spinner-${arc.id})`} />
          ))}
        </g>
      </svg>
    </output>
  );
}
